import React from "react";
import dayjs from "dayjs";
import { DateAndTimeConfigForReceipt } from "@elixir/types";
import { ReactComponent as GearRegularIcon } from "../../assets/icons/gear-regular.svg";
import ToggleSwitchComponent from "../../atoms/toggle-switch-component";
import RadioComponent from "../../atoms/radio-component";
import useReceiptCustomisationStore from "../../stores/receipt-customisation/receipt-customisation-store";
import ReceiptCounterPopup from "../receipt/receipt-counter-popup";
import { RadioInputSelector, TRadioOption } from "./columns-component";

type TProperty =
  | "uhid"
  | "payment_method"
  | "receipt_number"
  | "require_uhid"
  | "print_amount_in_words"
  | "bill_created_at"
  | "print_bill_created_by";

const PropertyWithToggleComponent = ({
  propertyId,
  propertyName,
  isOn,
  onChange,
}: {
  propertyId: TProperty;
  propertyName: string | React.ReactNode;
  isOn: boolean;
  onChange: (id: TProperty) => void;
}) => {
  return (
    <div className="flex justify-between items-center">
      {/* Body/Body 1/Regular */}
      <div className="text-14 font-400 leading-6">{propertyName}</div>
      <ToggleSwitchComponent
        toggleId={propertyId}
        isOn={isOn}
        handleSwitchChange={() => onChange(propertyId)}
      />
    </div>
  );
};

const OtherPreferencesComponent = () => {
  const { others, setOthers, clinicId, setColumns, columns } =
    useReceiptCustomisationStore();

  const {
    showUHID,
    showPaymentMethod,
    showReceiptNumber,
    requireUhid,
    print_amount_in_words,
    date_and_time,
    bill_created_at,
    print_bill_created_by,
  } = others;

  const handleChange = (property: TProperty) => {
    setOthers({
      showUHID: property === "uhid" ? !showUHID : showUHID,
      showPaymentMethod:
        property === "payment_method" ? !showPaymentMethod : showPaymentMethod,
      showReceiptNumber:
        property === "receipt_number" ? !showReceiptNumber : showReceiptNumber,
      requireUhid: property === "require_uhid" ? !requireUhid : requireUhid,
      print_amount_in_words:
        property === "print_amount_in_words"
          ? !print_amount_in_words
          : print_amount_in_words,
      date_and_time: date_and_time,
      bill_created_at:
        property === "bill_created_at" ? !bill_created_at : bill_created_at,
      print_bill_created_by:
        property === "print_bill_created_by"
          ? !print_bill_created_by
          : print_bill_created_by,
    });
  };

  const handleDateTimeChange = (
    selectedOption: DateAndTimeConfigForReceipt
  ) => {
    setOthers({
      showUHID,
      showPaymentMethod,
      showReceiptNumber,
      requireUhid,
      print_amount_in_words,
      bill_created_at,
      print_bill_created_by,
      date_and_time: selectedOption,
    });
  };

  const [showCounterPopup, setShowCounterPopup] =
    React.useState<boolean>(false);

  const handleCloseCounterPopup = () => {
    setShowCounterPopup(false);
  };

  const options: Array<TRadioOption> = [
    { id: "A4", label: "A4" },
    { id: "A5", label: "A5" },
  ];

  return (
    <>
      {showCounterPopup && (
        <ReceiptCounterPopup
          clinicId={clinicId}
          onClosePopup={handleCloseCounterPopup}
        />
      )}
      <div className="p-16">
        <div className="w-full flex items-center text-16 font-700 leading-8">
          Other Preferences
        </div>
        <div className="space-y-16 pt-16">
          <PropertyWithToggleComponent
            propertyId="uhid"
            propertyName="UHID"
            isOn={showUHID}
            onChange={handleChange}
          />
          <PropertyWithToggleComponent
            propertyId="payment_method"
            propertyName="Payment Method"
            isOn={showPaymentMethod}
            onChange={handleChange}
          />
          <PropertyWithToggleComponent
            propertyId="receipt_number"
            propertyName={
              <div className="flex justify-between items-center space-x-16">
                <div>Receipt Number</div>
                <GearRegularIcon
                  className="h-16 w-16 text-darwin-neutral-400 cursor-pointer"
                  onClick={() => {
                    setShowCounterPopup(true);
                  }}
                />
              </div>
            }
            isOn={showReceiptNumber}
            onChange={handleChange}
          />
          <PropertyWithToggleComponent
            propertyId="require_uhid"
            propertyName="Require Patient UHID"
            isOn={!!requireUhid}
            onChange={handleChange}
          />
          <PropertyWithToggleComponent
            propertyId="print_amount_in_words"
            propertyName="Print Amount in Words"
            isOn={!!print_amount_in_words}
            onChange={handleChange}
          />
          <PropertyWithToggleComponent
            propertyId="bill_created_at"
            propertyName="Bill Creation Time"
            isOn={!!bill_created_at}
            onChange={handleChange}
          />
          <PropertyWithToggleComponent
            propertyId="print_bill_created_by"
            propertyName="Print Bill Created By"
            isOn={!!print_bill_created_by}
            onChange={handleChange}
          />

          <div className="space-y-4">
            <div className="text-14 font-400 leading-6">Date & Time Format</div>
            <div className="flex flex-col space-y-8">
              <RadioComponent
                isSelected={
                  date_and_time === DateAndTimeConfigForReceipt.DATE_ONLY ||
                  !date_and_time
                }
                label={`Only Date`}
                onClick={() =>
                  handleDateTimeChange(DateAndTimeConfigForReceipt.DATE_ONLY)
                }
                labelClassName="text-14 font-400 leading-6"
              />
              <RadioComponent
                isSelected={
                  date_and_time === DateAndTimeConfigForReceipt.DATE_AND_TIME
                }
                label={`Date & Time`}
                onClick={() =>
                  handleDateTimeChange(
                    DateAndTimeConfigForReceipt.DATE_AND_TIME
                  )
                }
                labelClassName="text-14 font-400 leading-6"
              />
              <RadioComponent
                isSelected={
                  date_and_time ===
                  DateAndTimeConfigForReceipt.EXTENDED_DATE_AND_TIME
                }
                label={`Extended Date & Time`}
                onClick={() =>
                  handleDateTimeChange(
                    DateAndTimeConfigForReceipt.EXTENDED_DATE_AND_TIME
                  )
                }
                labelClassName="text-14 font-400 leading-6"
              />
            </div>
          </div>
          <RadioInputSelector
            title="page_size"
            onClick={setColumns}
            options={options}
            selectedId={columns.page_size}
          />
        </div>
      </div>
    </>
  );
};

export default OtherPreferencesComponent;
